/**
 * 会话内 tree API 缓存：同一 repo+ref+path 只请求一次，跨页面（RepoPage/FileBrowser）共享。
 * 解决侧边栏展开/折叠与右侧面板重复请求、导航后重新请求同一 path 的问题。
 */

import { get } from './client';

export interface TreeItem {
    name: string;
    path: string;
    sha: string;
    type: 'tree' | 'blob';
    mode: string;
}

export interface TreeData {
    sha: string;
    path: string;
    items: TreeItem[];
}

const cache = new Map<string, TreeData>();

function cacheKey(repoPath: string, ref: string, path: string): string {
    return `${repoPath}:${ref}:${path}`;
}

/**
 * 拉取 tree：命中缓存直接返回，未命中请求后写入缓存。
 */
export async function getTreeCached(
    repoPath: string,
    ref: string,
    path: string
): Promise<TreeData> {
    const key = cacheKey(repoPath, ref, path);
    const hit = cache.get(key);
    if (hit) return hit;
    const refEnc = encodeURIComponent(ref);
    const endpoint = path
        ? `/repos/${repoPath}/tree/${refEnc}/${path}`
        : `/repos/${repoPath}/tree/${refEnc}`;
    const data = await get<TreeData>(endpoint);
    cache.set(key, data);
    return data;
}

/**
 * repoPath 或 ref 变化时清空缓存（避免看到别的仓库/分支的数据）。
 */
export function clearTreeCache(repoPath?: string, ref?: string): void {
    if (!repoPath && !ref) {
        cache.clear();
        return;
    }
    const prefix =
        repoPath && ref ? `${repoPath}:${ref}:` : repoPath ? `${repoPath}:` : ref ? `:${ref}:` : '';
    if (!prefix) return;
    const match = prefix.startsWith(':') ? (k: string) => k.includes(prefix) : (k: string) => k.startsWith(prefix);
    for (const key of Array.from(cache.keys())) {
        if (match(key)) cache.delete(key);
    }
}
